import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip,
  TooltipButton,
  IconInfo,
  PasswordInput,
} from '@americanexpress/dls-react';
import { validateDlsInput } from '../../Validations/Validations';

/**
 * returns DLS input component
 * @param {*} props
 */
/* eslint complexity: ["error", 30] -- need to increase complexity */
const Password = (props) => {
  const {
    fieldLabel,
    fieldId,
    cssProperties,
    fieldValidation,
    defaultValue,
    fieldPlaceholder,
    showHideToggleProps,
    validations = [],
    setValidationErrors,
    validationErrors,
    setFocus,
    fieldRank = 1,
    formSubmissionPayload,
    operationsToPerform,
    operationCaller,
    dataObject,
  } = props;

  const hideButton = showHideToggleProps && showHideToggleProps.hideButton
    ? showHideToggleProps.hideButton
    : false;
  const defaultShow = fieldValidation && fieldValidation.defaultShow
    ? fieldValidation.defaultShow
    : false;
  const showLabel = showHideToggleProps && showHideToggleProps.showLabel
    ? showHideToggleProps.showLabel
    : 'Show';
  const hideLabel = showHideToggleProps && showHideToggleProps.hideLabel
    ? showHideToggleProps.hideLabel
    : 'Hide';

  const [data, setData] = useState('');
  const [customInvalidMsg, setCustomInvalidMsg] = useState('');
  const [status, setStatus] = useState('success');
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [validationErrState, dispatchErrState] = useReducer((prevState, payload) => ({ ...prevState, ...payload }), { validationErr: '', isValid: true });

  useEffect(() => {
    if (formSubmissionPayload && formSubmissionPayload[fieldId]) {
      setData(formSubmissionPayload[fieldId]);
    }
  }, [fieldId]);

  const handleInput = (event, val = null) => {
    const inputVal = event ? event.target.value : val;
    if (validations.length > 0) {
      setData(inputVal);
      const invalidMsg = validateDlsInput(inputVal, validations, { ...dataObject });
      if (invalidMsg) {
        dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
      } else {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    } else {
      setData(inputVal);
    }
  };

  const handleKeyPress = (event) => {
    if (fieldValidation.isNumeric) {
      // eslint-disable-next-line unicorn/better-regex -- need
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphabet) {
      const re = /^[A-Za-z]+$/;
      if (!re.test(event.key)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphaNumeric) {
      const alphaNu = /^[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}]*$/;
      if (alphaNu.test(event.key)) {
        event.preventDefault();
      }
    }
  };

  const invalidHandler = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      handleInput(e);
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    } else if (fieldValidation && fieldValidation.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${fieldId}`).focus();
      if (!e.target.value) {
        setCustomInvalidMsg(fieldValidation
          && fieldValidation.customInvalidMsg
          ? fieldValidation.customInvalidMsg : 'Please complete this field');
      } else {
        setCustomInvalidMsg(fieldValidation
          && fieldValidation.customInvalidMsg
          ? fieldValidation.customInvalidMsg : 'Please fill in this field');
      }
      setShowCustomInvalidMsg(true);
      setStatus('error');
    } else {
      e.target.setCustomValidity(`${customInvalidMsg}`);
    }
    // invoke some error message red color
  };
  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
    setStatus('success');
  };

  const handleOnBlur = (e) => {
    if (operationsToPerform?.onBlur?.length > 0 && validationErrState.isValid) {
      operationCaller(operationsToPerform.onBlur, {}, { value: e.target.value });
    }
  };

  useEffect(() => {
    // validationErr also can change multiple times, so need one isValid
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  }, [validationErrState.isValid]);

  return (
    <>
      <label className={fieldLabel.cssProperties.style} htmlFor={fieldId}>
        {fieldLabel.label}
      </label>
      {fieldValidation && fieldValidation.isToolTip && (
        <>
          <span> </span>
          <Tooltip
            id="ToolTip"
            triggerElement={
              <TooltipButton icon={<IconInfo size="sm" title="info" />} />
            }
          >
            {fieldValidation.toolTipInfo}
          </Tooltip>
        </>
      )}
      {!fieldValidation.required && (
        <span className={fieldLabel.cssProperties.style}>
          {fieldLabel.optionalLabel ? fieldLabel.optionalLabel : ' (Optional)'}
        </span>
      )}
      {fieldLabel && fieldLabel.subLabel && (
        <div>
          <label
            className={fieldLabel.cssProperties.subLabelStyle}
            htmlFor={fieldId}
          >
            {fieldLabel.subLabel}
          </label>
        </div>
      )}
      <PasswordInput
        id={fieldId}
        style={{
          border: '.0625rem solid #8e9092',
        }}
        status={status}
        title=""
        defaultValue={defaultValue}
        hideButton={hideButton}
        defaultShow={defaultShow}
        showLabel={showLabel}
        hideLabel={hideLabel}
        value={data}
        onChange={handleInput}
        placeholder={fieldPlaceholder}
        onKeyPress={handleKeyPress}
        required={fieldValidation.required}
        maxLength={fieldValidation.maxSize}
        minLength={fieldValidation.minSize}
        onInvalid={invalidHandler}
        onInput={onInputHandler}
        onBlur={handleOnBlur}
      />
      {
        validationErrState.validationErr
        && (
          <div id="alert-error" className={cssProperties.validationErrorStyle ? cssProperties.validationErrorStyle : 'alert dls-color-warning pad-1-t col-md-12'}>
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{validationErrState.validationErr}</span>
          </div>
        )
      }
      {showCustomInvalidMsg && (
        <div id="alert-error" className="alert dls-color-warning pad-1-t">
          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
          <span>{customInvalidMsg}</span>
        </div>
      )}
    </>
  );
};

Password.propTypes = {
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  defaultValue: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  fieldPlaceholder: PropTypes.string,
  showHideToggleProps: PropTypes.objectOf(PropTypes.shape),
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  operationCaller: PropTypes.func,
  dataObject: PropTypes.objectOf(PropTypes.shape),
};

export default Password;
